/*
 * Launch-pricing timers for drop-hero and drop-countdown-bar.
 *
 * <drop-timer data-start="ISO" data-end="ISO" hidden>
 *   Hidden before hydration. Sets data-state="before" (now < start) or "live" (start <= now < end), and hides itself
 *   at the end. While live it writes the remaining time into [data-clock] ("59:59", "09:59", "1:00:00": minutes always
 *   padded, hours only when > 0) and into [data-unit="h|m|s"] boxes ("00"), and toggles data-no-hours when hours = 0.
 *   A tiny inline script after each element sets hidden/data-state before first paint, so nothing shifts when this
 *   deferred file runs.
 *
 * <drop-reveal data-start="ISO" hidden>
 *   Hidden until start, then shown for good (e.g. the "Just dropped" chip). No or unparseable start: shown.
 *
 * All elements share ONE loop aligned to the clock second, paused while the tab is hidden.
 * QA: change data-start / data-end in devtools and call el.init() to replay the states.
 */
(() => {
  if (!window.customElements || customElements.get('drop-timer')) return;

  const timers = new Set();
  let handle = 0;

  const pad = (n) => String(n).padStart(2, '0');

  // Whole seconds left → { h, m, s }
  const parts = (seconds) => {
    const t = Math.max(0, Math.floor(seconds));
    return { h: Math.floor(t / 3600), m: Math.floor((t % 3600) / 60), s: t % 60 };
  };

  const format = (seconds) => {
    const { h, m, s } = parts(seconds);
    return (h > 0 ? h + ':' : '') + pad(m) + ':' + pad(s);
  };

  // Unparseable end (NaN) counts as ended; unparseable start counts as already started.
  const stateAt = (now, start, end) => (!(end > now) ? 'ended' : now < start ? 'before' : 'live');

  const tickAll = () => {
    const now = Date.now();
    timers.forEach((el) => el.tick(now));
  };

  const schedule = () => {
    if (handle || !timers.size || document.hidden) return;
    handle = setTimeout(loop, 1010 - (Date.now() % 1000));
  };

  function loop() {
    handle = 0;
    tickAll();
    schedule();
  }

  const resync = () => {
    clearTimeout(handle);
    handle = 0;
    if (document.hidden) return;
    tickAll();
    schedule();
  };

  const track = (el) => {
    timers.add(el);
    schedule();
  };

  const whenReady = (fn) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  };

  document.addEventListener('visibilitychange', resync);
  window.addEventListener('pageshow', (event) => { if (event.persisted) resync(); });

  class DropTimer extends HTMLElement {
    connectedCallback() { whenReady(() => this.init()); }

    disconnectedCallback() { timers.delete(this); }

    init() {
      if (!this.isConnected) return;
      this.start = Date.parse(this.dataset.start || '') || 0;
      this.end = Date.parse(this.dataset.end || '');
      this.clock = this.querySelector('[data-clock]');
      this.units = this.querySelectorAll('[data-unit]');
      if (this.tick(Date.now())) track(this);
    }

    // Returns false once the timer is done (ended), so the loop drops it.
    tick(now) {
      const state = stateAt(now, this.start, this.end);

      if (state === 'ended') {
        this.hidden = true;
        this.dataset.state = state;
        timers.delete(this);
        return false;
      }

      if (this.dataset.state !== state) this.dataset.state = state;
      if (this.hidden) this.hidden = false;

      if (state === 'live') {
        const left = (this.end - now) / 1000;
        const p = parts(left);
        this.toggleAttribute('data-no-hours', p.h === 0);

        if (this.clock) {
          const text = format(left);
          if (this.clock.textContent !== text) this.clock.textContent = text;
        }

        this.units.forEach((el) => {
          const value = pad(p[el.dataset.unit] || 0);
          if (el.textContent !== value) el.textContent = value;
        });
      }

      return true;
    }
  }

  class DropReveal extends HTMLElement {
    connectedCallback() { whenReady(() => this.init()); }

    disconnectedCallback() { timers.delete(this); }

    init() {
      if (!this.isConnected) return;
      this.start = Date.parse(this.dataset.start || '');
      if (this.tick(Date.now())) track(this);
    }

    tick(now) {
      if (now < this.start) {
        if (!this.hidden) this.hidden = true;
        return true;
      }
      this.hidden = false;
      timers.delete(this);
      return false;
    }
  }

  // Pure helpers, exposed for tests: customElements.get('drop-timer').format(599) === '09:59'
  DropTimer.format = format;
  DropTimer.parts = parts;
  DropTimer.stateAt = stateAt;

  customElements.define('drop-timer', DropTimer);
  if (!customElements.get('drop-reveal')) customElements.define('drop-reveal', DropReveal);
})();
